import React, { useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import SendIcon from '@mui/icons-material/Send'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import WavingHandIcon from '@mui/icons-material/WavingHand'
import { collectPageContext, collectLightPageContext } from './pageContextCollector'
import { copilotApiEndpoint } from './variables'
import { siReportCollector, ghgReportCollector, fetchBackendReportData } from './reportDataCollector'
import DataSourceManager from './DataSourceManager'
import { consumeSseStream, parseErrorResponse } from './sseParser'



function uid() {
    return Math.random().toString(36).slice(2, 9)
}

const controlIconProps = { sx: { fontSize: 18, color: '#12332a' } }
const fabIconProps = { sx: { fontSize: 26, color: '#1b3b2b' } }
const sendIconProps = { sx: { fontSize: 18, color: '#ffffff' } }

function getUploadEndpoint(endpoint) {
    try {
        const url = new URL(endpoint)
        if (url.pathname.endsWith('/chat')) {
            url.pathname = url.pathname.replace(/\/chat$/, '/upload')
        } else {
            url.pathname = '/api/upload'
        }
        return url.toString()
    } catch {
        return endpoint.replace(/\/chat$/, '/upload')
    }
}

function readFileAsBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
            const result = reader.result
            if (typeof result === 'string') {
                const parts = result.split(',')
                resolve(parts[1] || '')
            } else {
                reject(new Error('Failed to read file'))
            }
        }
        reader.onerror = () => reject(new Error('Failed to read file'))
        reader.readAsDataURL(file)
    })
}

export default function Copilot({
    apiEndpoint = copilotApiEndpoint,
    includePageContext = true,
    lightweightContext = false,
    customContext,
    siReportData,
    ghgReportData,
    backendApiUrl,
    reportId,
    toolType,
    authToken,
    organizationId,
    enabled = true,
    onMessage,
    onError
} = {}) {
    const [open, setOpen] = useState(false)
    const [minimized, setMinimized] = useState(false)
    const [maximized, setMaximized] = useState(false)
    const [showSettings, setShowSettings] = useState(false)
    const [messages, setMessages] = useState([])
    const [input, setInput] = useState('')
    const [streaming, setStreaming] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [uploadedFile, setUploadedFile] = useState(null)
    const [fileError, setFileError] = useState(null)
    const [fetchedReportData, setFetchedReportData] = useState(undefined)
    const scrollRef = useRef(null)
    const fileInputRef = useRef(null)

    useEffect(() => {
        if (backendApiUrl && reportId && toolType && authToken) {
            fetchBackendReportData(backendApiUrl, reportId, toolType, authToken)
                .then(data => setFetchedReportData(data))
                .catch(err => console.error('Failed to fetch report context', err))
        }
    }, [backendApiUrl, reportId, toolType, authToken])

    const resetState = () => {
        setMessages([])
        setInput('')
        setUploadedFile(null)
        setFileError(null)
        setStreaming(false)
        setUploading(false)
    }

    const sendMessage = async (question, assistantId) => {
        try {
            let pageContext = customContext
            let collectedSiReportData = await siReportCollector(siReportData)
            let collectedGhgReportData = await ghgReportCollector(ghgReportData)

            // Override with pre-fetched backend data if available
            if (fetchedReportData) {
                if (toolType === 'si') collectedSiReportData = fetchedReportData
                if (toolType === 'ghg') collectedGhgReportData = fetchedReportData
            }

            if (includePageContext && !customContext) {
                pageContext = lightweightContext ? collectLightPageContext() : collectPageContext()
            }

            const payload = { question, history: messages, organizationId }
            if (pageContext) {
                payload.pageContext = { ...pageContext, uploadedFile }
            } else if (uploadedFile) {
                payload.pageContext = { uploadedFile }
            }

            if (collectedSiReportData?.content) {
                payload.siReportData = collectedSiReportData.content
            }

            if (collectedGhgReportData?.content) {
                payload.ghgReportData = collectedGhgReportData.content
            }

            const res = await fetch(apiEndpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            })

            if (!res.ok) {
                throw new Error(await parseErrorResponse(res))
            }

            const contentType = res.headers.get('content-type') || ''
            const isJson = contentType.includes('application/json')
            const isSse = contentType.includes('text/event-stream')

            if (isJson) {
                const data = await res.json()
                const assistantMessage = {
                    id: assistantId,
                    role: 'assistant',
                    content: data.answer || '',
                    timestamp: new Date().toISOString(),
                    paragraphs: data.paragraphs || [],
                    followUpQuestions: data.followUpQuestions || [],
                    outOfScope: data.outOfScope || false,
                    citations: data.citations || []
                }
                setMessages((prev) => prev.map((msg) => (msg.id === assistantId ? assistantMessage : msg)))
                if (onMessage) onMessage(assistantMessage)
            } else if (isSse && res.body) {
                const reader = res.body.getReader()
                let accumulated = ''

                await consumeSseStream(reader, (event, data) => {
                    if (event === 'delta' && data?.content) {
                        accumulated += data.content
                        setMessages((prev) =>
                            prev.map((msg) =>
                                msg.id === assistantId ? { ...msg, content: accumulated } : msg
                            )
                        )
                        return
                    }

                    if (event === 'final') {
                        const assistantMessage = {
                            id: assistantId,
                            role: 'assistant',
                            content: data?.answer || accumulated,
                            timestamp: new Date().toISOString(),
                            paragraphs: data?.paragraphs || [],
                            followUpQuestions: data?.followUpQuestions || [],
                            outOfScope: data?.outOfScope || false,
                            citations: data?.citations || []
                        }
                        setMessages((prev) =>
                            prev.map((msg) => (msg.id === assistantId ? assistantMessage : msg))
                        )
                        if (onMessage) onMessage(assistantMessage)
                        return
                    }

                    if (event === 'error') {
                        throw new Error(data?.error || 'The assistant could not complete that request.')
                    }
                })
            } else if (res.body) {
                const text = await res.text()
                if (text.trim()) {
                    setMessages((prev) =>
                        prev.map((msg) => (msg.id === assistantId ? { ...msg, content: text } : msg))
                    )
                }
            }
        } catch (err) {
            const error = err
            const errorContent = 'Error: ' + error.message
            setMessages((prev) =>
                prev.map((msg) =>
                    msg.id === assistantId ? { ...msg, content: errorContent } : msg
                )
            )
            if (onError) onError(error)
        } finally {
            setStreaming(false)
        }
    }

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight
        }
    }, [messages, minimized])

    const send = async () => {
        const question = input.trim()
        if (!question) return
        const userMsg = { id: uid(), role: 'user', content: question, timestamp: new Date().toISOString() }
        setMessages((m) => [...m, userMsg])
        setInput('')
        setOpen(true)
        setMinimized(false)

        // create assistant placeholder
        const assistantId = uid()
        const assistantMsg = { id: assistantId, role: 'assistant', content: '', timestamp: new Date().toISOString() }
        setMessages((m) => [...m, assistantMsg])
        setStreaming(true)
        await sendMessage(question, assistantId)
    }

    if (!enabled) return null

    return (
        <div>
            <div className={`copilot-root ${maximized ? 'maximized' : ''}`} style={{ zIndex: 9999 }}>
                {!open && (
                    <button
                        className="copilot-button"
                        onClick={() => setOpen(true)}
                        aria-label="Open Copilot"
                        title="Ask me anything about this page"
                    >
                        <ChatBubbleOutlineIcon {...fabIconProps} />
                    </button>
                )}

                {open && (
                    <div className={`copilot-panel ${minimized ? 'min' : ''}`}>
                        <div className="copilot-header">
                            <div className="title">GE3S Sustainability Copilot</div>
                            <div className="controls">
                                {organizationId && (
                                    <button onClick={() => setShowSettings(!showSettings)} title="Settings" aria-label="Settings">
                                        <SettingsOutlinedIcon {...controlIconProps} />
                                    </button>
                                )}
                                <button onClick={() => setMinimized((s) => !s)} title={minimized ? 'Expand' : 'Minimize'} aria-label={minimized ? 'Expand' : 'Minimize'}>
                                    {minimized ? <KeyboardArrowUpIcon {...controlIconProps} /> : <KeyboardArrowDownIcon {...controlIconProps} />}
                                </button>
                                <button
                                    onClick={() => {
                                        setMaximized((s) => !s)
                                        if (minimized) setMinimized(false)
                                        setOpen(true)
                                    }}
                                    title={maximized ? 'Restore' : 'Maximize'}
                                    aria-label={maximized ? 'Restore' : 'Maximize'}
                                >
                                    {maximized ? <CloseFullscreenIcon {...controlIconProps} /> : <OpenInFullIcon {...controlIconProps} />}
                                </button>
                                <button onClick={() => {
                                    resetState()
                                    setOpen(false)
                                }} title="Close" aria-label="Close">
                                    <CloseIcon {...controlIconProps} />
                                </button>
                            </div>
                        </div>

                        {!minimized && showSettings ? (
                            <div className="copilot-body" style={{flex: 1, overflow: 'hidden'}}>
                                <DataSourceManager organizationId={organizationId} onClose={() => setShowSettings(false)} />
                            </div>
                        ) : !minimized && (
                            <div className="copilot-body">
                                <div className="messages" ref={scrollRef}>
                                    {messages.length === 0 && (
                                        <div className="message-empty">
                                            <p className="message-empty-greeting">
                                                <WavingHandIcon sx={{ fontSize: 16, color: '#567469', verticalAlign: 'text-bottom' }} />
                                                {' '}Hi! Ask me anything about this page.
                                            </p>
                                        </div>
                                    )}
                                    {messages.map((m) => (
                                        m && m.content &&
                                        <div key={m.id} className={`message ${m.role}`}>
                                            {m.role === 'assistant' && m.paragraphs && m.paragraphs.length > 0 ? (
                                                <div className="bubble-content">
                                                    {m.paragraphs.map((paragraph, idx) => (
                                                        <div key={idx} className="paragraph">
                                                            <ReactMarkdown
                                                                components={{
                                                                    p: ({ children }) => <p>{children}</p>,
                                                                    strong: ({ children }) => <strong>{children}</strong>,
                                                                    em: ({ children }) => <em>{children}</em>,
                                                                    code: ({ children }) => <code>{children}</code>,
                                                                    pre: ({ children }) => <pre>{children}</pre>,
                                                                    ul: ({ children }) => <ul>{children}</ul>,
                                                                    ol: ({ children }) => <ol>{children}</ol>,
                                                                    li: ({ children }) => <li>{children}</li>,
                                                                    h1: ({ children }) => <h4 style={{ margin: '8px 0 4px 0' }}>{children}</h4>,
                                                                    h2: ({ children }) => <h5 style={{ margin: '8px 0 4px 0' }}>{children}</h5>,
                                                                    h3: ({ children }) => <h6 style={{ margin: '8px 0 4px 0' }}>{children}</h6>,
                                                                    blockquote: ({ children }) => <blockquote style={{ borderLeft: '3px solid #0078d4', paddingLeft: '8px', marginLeft: '0' }}>{children}</blockquote>,
                                                                    a: ({ children, href }) => <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#4eb3ff' }}>{children}</a>
                                                                }}
                                                            >
                                                                {paragraph}
                                                            </ReactMarkdown>
                                                        </div>
                                                    ))}
                                                </div>

                                            ) : (
                                                <div className="bubble">{m.content}</div>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".pdf,.txt,.md,.json,.js,.ts,.tsx,.py,.java,.c,.cpp,.cs,.html,.css,.scss,.xml,.yml,.yaml"
                                    disabled={uploading}
                                    style={{ display: 'none' }}
                                    onChange={async (e) => {
                                        setFileError(null)
                                        const file = e.target.files?.[0]
                                        if (!file) {
                                            setUploadedFile(null)
                                            return
                                        }

                                        const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name)
                                        const textLike =
                                            file.type.startsWith('text') ||
                                            /\.(md|txt|json|js|ts|tsx|py|java|c|cpp|cs|html|css|scss|xml|yaml|yml)$/i.test(file.name)

                                        if (!isPdf && !textLike) {
                                            setUploadedFile(null)
                                            setFileError('Only PDF or plain text/source files are supported.')
                                            return
                                        }

                                        try {
                                            setUploading(true)

                                            const uploadEndpoint = getUploadEndpoint(apiEndpoint)
                                            let payload

                                            let textContent = undefined
                                            if (isPdf) {
                                                const data = await readFileAsBase64(file)
                                                payload = { name: file.name, type: file.type || 'application/pdf', data }
                                            } else {
                                                textContent = await file.text()
                                                payload = { name: file.name, type: file.type || 'text/plain', content: textContent }
                                            }

                                            const response = await fetch(uploadEndpoint, {
                                                method: 'POST',
                                                headers: { 'Content-Type': 'application/json' },
                                                body: JSON.stringify(payload)
                                            })

                                            if (!response.ok) {
                                                const errorText = await response.text()
                                                throw new Error(errorText || 'Failed to upload document')
                                            }

                                            const data = await response.json()
                                            setUploadedFile({
                                                id: data.sourceId || data.id,
                                                name: file.name.length > 6
                                                    ? file.name.slice(0, 3) + "..."
                                                    : file.name,
                                                type: file.type,
                                                content: textContent,
                                                preview: data.preview
                                            })
                                        } catch (error) {
                                            setUploadedFile(null)
                                            setFileError(error instanceof Error ? error.message : 'Failed to process uploaded file.')
                                        } finally {
                                            setUploading(false)
                                        }
                                    }}
                                />
                                {uploading && <div className="file-status">Uploading and processing your file...</div>}
                                {uploadedFile && (
                                    <div className="file-summary">
                                        <div>
                                            <strong>{uploadedFile.name}</strong>
                                        </div>
                                    </div>
                                )}
                                {streaming && (
                                    <div className="typing-indicator">
                                        <div className="dots">
                                            <span></span>
                                            <span></span>
                                            <span></span>
                                        </div>
                                    </div>
                                )}
                                {fileError && <div className="file-error">{fileError}</div>}

                                <div className="composer">
                                    <button
                                        className="add-file-btn"
                                        onClick={() => fileInputRef.current?.click()}
                                        title="Add file"
                                        aria-label="Add file"
                                    >
                                        <AddIcon sx={{ fontSize: 20, color: '#1b3b2b' }} />
                                    </button>
                                    <input
                                        value={input}
                                        onChange={(e) => setInput(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault()
                                                send()
                                            }
                                        }}
                                        placeholder="Ask about this page..."
                                        disabled={streaming}
                                    />
                                    <button onClick={send} disabled={streaming} className="send" aria-label="Send message">
                                        {streaming ? <HourglassEmptyIcon {...sendIconProps} /> : <SendIcon {...sendIconProps} />}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <style>{`
        .copilot-root{position:fixed;bottom:24px;right:24px}
        .copilot-root.maximized{top:12px;right:12px;left:12px;bottom:12px}
        .copilot-button{background:linear-gradient(135deg,#eaf9ee,#f6fff9);color:#1b3b2b;border:none;border-radius:999px;width:56px;height:56px;font-size:22px;box-shadow:0 8px 24px rgba(27,59,43,0.08);cursor:pointer;transition:transform 0.18s;font-family:inherit;display:inline-flex;align-items:center;justify-content:center}
        .copilot-button:hover{transform:scale(1.05)}
        .copilot-button:active{transform:scale(0.95)}
        .copilot-panel{width:360px;height:520px;background:linear-gradient(135deg,#fbfff9,#f0fff4);color:#12332a;border-radius:12px;box-shadow:0 12px 40px rgba(8,24,12,0.08);overflow:hidden;display:flex;flex-direction:column;font-family:inherit}
        .copilot-root.maximized .copilot-panel{width:100%;height:100%;border-radius:10px}
        .copilot-panel.min{height:60px;width:320px}
        .copilot-header{display:flex;align-items:center;justify-content:space-between;padding:12px 14px;background:linear-gradient(90deg,#f0fff4,#eaf9ee);border-bottom:1px solid rgba(18,51,42,0.06)}
        .copilot-header .title{font-weight:600;font-size:14px}
        .copilot-header .controls button{background:transparent;border:none;color:#12332a;margin-left:4px;cursor:pointer;padding:6px;border-radius:6px;transition:background 0.14s;font-size:14px;display:inline-flex;align-items:center;justify-content:center;min-width:32px;min-height:32px}
        .copilot-header .controls button:hover{background:rgba(18,51,42,0.04)}
        .copilot-body{display:flex;flex-direction:column;flex:1;min-height:0}
        .messages{padding:12px;flex:1;overflow-y:auto;display:flex;flex-direction:column;gap:8px}
        .message-empty{text-align:center;padding:20px;color:#567469;font-size:13px}
        .message-empty-greeting{display:inline-flex;align-items:center;justify-content:center;gap:4px;margin:0}
        .message{margin:0;display:flex;flex-direction:column}
        .message.user .bubble{align-self:flex-end;background:#e6fff0;color:#083822}
        .message.assistant .bubble{align-self:flex-start;background:#ffffff;color:#083822;border:1px solid rgba(18,51,42,0.04)}
        .bubble{max-width:85%;padding:10px 12px;border-radius:10px;word-wrap:break-word;font-size:13px;line-height:1.4}
        .follow-up-questions{display:flex;flex-direction:column;gap:6px;margin-top:8px;max-width:85%;align-self:flex-start}
        .follow-up-btn{padding:8px 12px;background:#1a3a52;color:#94c9f8;border:1px solid #0078d4;border-radius:6px;font-size:12px;cursor:pointer;transition:all 0.2s;font-family:inherit;text-align:left;line-height:1.3;font-weight:500}
        .follow-up-btn:hover:not(:disabled){background:#243d52;border-color:#4eb3ff;color:#a8d4ff}
        .follow-up-btn:active:not(:disabled){background:#1a3a52}
        .follow-up-btn:disabled{opacity:0.6;cursor:not-allowed}
        .ts{font-size:11px;color:#9fb0c6;margin-top:4px;padding:0 4px}
        .file-upload{display:flex;flex-direction:column;gap:8px;padding:10px 14px;border-bottom:1px solid rgba(18,51,42,0.04);background:transparent}
        .file-label{color:#12332a;font-size:13px;display:flex;flex-direction:column;gap:6px}
        .file-label input{margin-top:4px}
        .file-summary{display:flex;flex-direction:column;gap:8px;font-size:13px;color:#567469;}
        .file-summary>div{display:flex;align-items:center;gap:6px;flex-wrap:wrap}
        .file-summary button{background:transparent;border:1px solid rgba(18,51,42,0.08);color:#12332a;padding:4px 8px;border-radius:6px;cursor:pointer}
        .file-summary button:hover{border-color:#7dbf9a}
        .file-status{font-size:12px;color:#567469}
        .file-preview{display:block;margin-top:4px;color:#6f9e88;font-size:12px;max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
        .file-error{color:#d85a5a;font-size:12px}
        .citations{margin-top:8px;padding:10px 12px;border-radius:10px;background:rgba(0,0,0,0.02);border:1px solid rgba(18,51,42,0.02)}
        .citation-heading{font-size:12px;color:#567469;font-weight:600;margin-bottom:6px}
        .citation-list{margin:0;padding:0;list-style:none;display:flex;flex-direction:column;gap:4px}
        .citation-item{font-size:12px;color:#567469}
        .composer{display:flex;padding:10px;border-top:1px solid rgba(18,51,42,0.04);gap:8px;background:transparent;align-items:center}
        .composer input{flex:1;padding:10px 12px;border-radius:8px;border:1px solid rgba(18,51,42,0.06);background:#ffffff;color:#083822;font-size:13px;font-family:inherit}
        .composer input:focus{outline:none;border-color:#7dbf9a;box-shadow:0 0 0 4px rgba(125,191,154,0.08)}
        .composer input:disabled{opacity:0.6;cursor:not-allowed}
        .composer .send{background:linear-gradient(180deg,#2b8f5a,#1e6e45);color:white;border:none;padding:8px 12px;border-radius:8px;cursor:pointer;font-size:14px;transition:transform 0.12s;display:inline-flex;align-items:center;justify-content:center;min-width:40px;min-height:40px}
        .composer .send:hover:not(:disabled){transform:translateY(-1px)}
        .composer .send:disabled{opacity:0.6;cursor:not-allowed}
        .add-file-btn{width:36px;height:36px;border-radius:8px;border:1px dashed rgba(18,51,42,0.12);background:linear-gradient(180deg,#f6fff9,#effff4);color:#1b3b2b;font-size:20px;cursor:pointer;display:inline-flex;align-items:center;justify-content:center}
        .add-file-btn:hover{border-color:rgba(18,51,42,0.18);transform:scale(1.02)}
        .typing{display:inline-flex;gap:4px;align-items:center}
        .typing .dot{width:6px;height:6px;border-radius:50%;background:#9bd1ff;display:inline-block;animation:blink 1s infinite}
        .typing .dot:nth-child(2){animation-delay:.2s}
        .typing .dot:nth-child(3){animation-delay:.4s}
        .bubble-content{display:flex;flex-direction:column;gap:8px;max-width:85%;padding:10px 12px;border-radius:10px;background:#f7fff9;border:1px solid rgba(18,51,42,0.04);color:#083822}
        .paragraph{font-size:13px;line-height:1.6}
        .paragraph p{margin:0;padding:0}
        .paragraph strong{color:#2b8f5a;font-weight:600}
        .paragraph em{color:#567469;font-style:italic}
        .paragraph code{background:#f0fff4;color:#0b3b2a;padding:2px 6px;border-radius:4px;font-family:'Courier New',monospace;font-size:12px}
        .paragraph pre{background:#f0fff4;color:#0b3b2a;padding:8px;border-radius:6px;overflow-x:auto;margin:4px 0;font-family:'Courier New',monospace;font-size:12px}
        .paragraph ul,.paragraph ol{margin:6px 0;padding-left:20px;color:#083822}
        .paragraph li{margin:3px 0}
        .paragraph h4,.paragraph h5,.paragraph h6{margin:8px 0 4px 0;color:#2b8f5a;font-weight:600}
        .paragraph blockquote{border-left:3px solid #7dbf9a;padding-left:10px;margin:6px 0;color:#567469;font-style:italic}
        .paragraph a{color:#2b8f5a;text-decoration:underline}
        .paragraph a:hover{color:#3aa77a}
        .citation-excerpt{font-size:11px;color:#567469;margin-top:4px;padding:4px 8px;background:rgba(0,0,0,0.02);border-radius:4px;max-height:40px;overflow:hidden;text-overflow:ellipsis}
        @keyframes blink{0%{opacity:.2}50%{opacity:1}100%{opacity:.2}}
        @media(max-width:480px){.copilot-panel{width:calc(100vw - 32px);height:70vh;max-height:600px}}
        .typing-indicator{display:flex;align-items:center;gap:8px;color:#666;font-size:14px;margin-left:20px}
        .dots{display:flex;gap:4px}
        .dots span{width:6px;height:6px;border-radius:50%;background:currentColor;animation:typing 1.4s infinite ease-in-out}
        .dots span:nth-child(1){animation-delay:0s}
        .dots span:nth-child(2){animation-delay:.2s}
        .dots span:nth-child(3){animation-delay:.4s}
        @keyframes typing{0%,80%,100%{transform:translateY(0);opacity:.4}40%{transform:translateY(-6px);opacity:1}}
      `}</style>
        </div>
    )
}
